import { useState } from 'react'
import { Button } from '@/components/catalyst-ui/button'
import { Input } from '@/components/catalyst-ui/input'
import { Field, Label, } from '@/components/catalyst-ui/fieldset'
import { Textarea } from '@/components/catalyst-ui/textarea'
import { Select } from '@/components/catalyst-ui/select'
import { Dialog, DialogTitle, DialogDescription, DialogBody, DialogActions } from '@/components/catalyst-ui/dialog'
import { XMarkIcon } from '@heroicons/react/24/solid'
export default function AddDNSRecordModal({ open, onClose, onAdd, loading = false }) {
  const [recordType, setRecordType] = useState('A - Address record')
  const [host, setHost] = useState('')
  const [value, setValue] = useState('')
  const [ttl, setTtl] = useState('600')
  const [priority, setPriority] = useState('10')
  const [notes, setNotes] = useState('')

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Validation
    if (!host.trim() && !value.trim()) {
      alert('Please fill in the required fields')
      return
    }
    
    const newRecord = {
      type: recordType.split(' - ')[0],
      host: host || '@',
      value,
      ttl: parseInt(ttl),
      priority: recordType.startsWith('MX') ? parseInt(priority) : null,
      notes,
      id: Date.now() // Add unique ID
    }
    
    // Call onAdd callback
    onAdd(newRecord)
    
    // Reset form
    setRecordType('A - Address record')
    setHost('')
    setValue('')
    setTtl('600')
    setPriority('10')
    setNotes('')
    
    // Close modal
    onClose()
  }

  const recordTypes = [
    'A - Address record',
    'AAAA - IPv6 Address record', 
    'CNAME - Canonical name record',
    'MX - Mail exchange record',
    'TXT - Text record',
    'NS - Name server record',
    'PTR - Pointer record',
    'SRV - Service locator record'
  ]

  return (
    <Dialog open={open} onClose={onClose} className="!rounded-xl p-5 !bg-[#FAFAFA]">
      <div className="flex items-center justify-between">
        <DialogTitle className="!text-lg !font-semibold !text-[#09090B]">Manage DNS Records</DialogTitle>
        <button
          onClick={onClose}
          className="text-black hover:text-zinc-600 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 rounded-full"
        >
          <XMarkIcon className="w-5 h-5 -mt-6 -mr-2.5" />
        </button>
      </div>
      <DialogBody className="!mt-2">
        <DialogDescription className="!text-sm !font-medium !text-[#A1A1AA]">
          Here you can configure custom DNS records for your domain.
        </DialogDescription>
        <form onSubmit={handleSubmit} className="space-y-3 mt-4">
          {/* Type Field */}
          <Field>
            <Label className="text-sm font-medium text-gray-700">Type</Label>
            <Select
              value={recordType}
              onChange={(e) => setRecordType(e.target.value)}
            >
              {recordTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
          </Field>

          {/* Host Field */}
          <Field>
            <Label className="text-sm font-medium text-gray-700">Host</Label>
            <p className="mt-2 text-sm font-medium text-[#A1A1AA]">
              Leave blank to create a record for the root domain. Use * to create a wildcard. 
              Please note that ALIAS records do not support wildcards.
            </p>
            <div className="mt-1 flex items-center gap-2">
              <Input
                type="text"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                placeholder="*"
              />
            </div>
            <p className="mt-1 text-sm font-medium text-[#71717B]">.domainname.com</p>
          </Field>

          {/* Answer/Value Field */}
          <Field>
            <Label className="text-sm font-medium text-gray-700">Answer / Value</Label>
            <Input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder=""
            />
          </Field>

          {/* Priority Field (only for MX records) */}
          {recordType.startsWith('MX') && (
            <Field>
              <Label className="text-sm font-medium text-gray-700">Priority</Label>
              <Input
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                placeholder="10"
              />
              <p className="mt-1 text-sm font-medium text-[#A1A1AA]">
                Lower values have higher priority
              </p>
            </Field>
          )}

          {/* TTL Field */}
          <Field>
            <Label className="text-sm font-medium text-gray-700">TTL</Label>
            <Input
              type="number"
              value={ttl}
              onChange={(e) => setTtl(e.target.value)}
              placeholder="600"
            />
          </Field>

          {/* Notes Field */}
          <Field>
            <Label className="text-sm font-medium text-gray-700">Notes</Label>
            <p className="mt-1 text-sm font-medium text-[#A1A1AA] mb-2">
              This is for your own use and does not affect DNS
            </p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder=""
            />
          </Field>
        </form>
      </DialogBody>
      <DialogActions className="!mt-4 !flex !flex-col sm:!flex-row !gap-3">
        <Button
          type="button"
          onClick={onClose}
          outline
          disabled={loading}
          className="w-full sm:flex-1 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          onClick={handleSubmit}
          color="dark"
          disabled={loading}
          className="w-full sm:flex-1 data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2"
        >
          {loading ? 'Adding...' : 'ADD Record'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}